import type { CoverageStatus, RunSource, RunStatus, RunSummary, TestResult, ValidationOutcome } from 'fuel-benchmark-contracts';
import { runSourceSchema, runStatusSchema } from 'fuel-benchmark-contracts';
import type { TFunction } from 'i18next';
import type { StatusTone } from '../../components/ui/StatusText';
import { formatDecimal } from '../../lib/format';
import { trafficSummary } from '../conditions/condition-display';

export const RUN_STATUSES = runStatusSchema.options;
export const RUN_SOURCES = runSourceSchema.options;

/**
 * Colour discipline (plan §2.2): a run's verdict is exactly the thing that has to catch the eye. INVALID is
 * red, a warning amber, a clean run green, a run still in flight blue, and a draft — the expected state of
 * unfinished work — stays muted.
 */
export const runStatusTone = (status: RunStatus): StatusTone => {
  switch (status) {
    case 'VALID':
      return 'success';
    case 'VALID_WITH_WARNING':
      return 'warning';
    case 'INVALID':
      return 'danger';
    case 'SUBMITTED':
      return 'info';
    case 'DRAFT':
      return 'muted';
  }
};

export const outcomeTone = (outcome: ValidationOutcome): StatusTone => {
  switch (outcome) {
    case 'PASS':
      return 'success';
    case 'WARN':
      return 'warning';
    case 'FAIL':
      return 'danger';
    // A rule that had nothing to measure against is not a verdict worth colouring.
    case 'SKIPPED':
      return 'muted';
  }
};

/** The verdict a coverage cell shows, with `—` (not tested) left deliberately quiet. */
export const coverageTone = (status: CoverageStatus): StatusTone => {
  switch (status) {
    case 'VALID':
      return 'success';
    case 'VALID_WITH_WARNING':
      return 'warning';
    case 'INVALID':
      return 'danger';
    case 'NOT_TESTED':
      return 'muted';
  }
};

/** The matrix glyphs of plan §7. */
export const COVERAGE_GLYPHS: Record<CoverageStatus, string> = {
  VALID: '✓',
  VALID_WITH_WARNING: '!',
  INVALID: '×',
  NOT_TESTED: '—',
};

/** An import-sourced run is worth noticing; a manually entered one is the norm. */
export const runSourceTone = (source: RunSource): StatusTone => (source === 'IMPORT' ? 'info' : 'muted');

/**
 * `21.2 L/100km` — one decimal, the precision the executive brief quotes. The stored figure keeps four, so
 * nothing is lost; this is only how a result reads.
 */
export const formatResult = (result: TestResult | null): string => (result === null ? '—' : `${formatDecimal(result.value, 1)} ${result.unit}`);

/**
 * `Summer · Day · A/C on · Mixed · High traffic` — the same one-line condition summary the scenario table
 * uses, including the traffic the scenario prescribes when it prescribes one. This describes the *scenario*
 * the run was measured under; what the run itself recorded is in its conditions.
 */
export const runConditionsSummary = (run: Pick<RunSummary, 'scenario'>, t: TFunction): string => {
  const { scenario } = run;
  const tail =
    scenario.testType === 'IDLE'
      ? `${t('enums.testType.IDLE')} ${scenario.idleDurationMin ?? '?'} min`
      : scenario.routeType === null
        ? t('common.notSet')
        : t(`enums.routeType.${scenario.routeType}`);

  return [
    t(`enums.season.${scenario.season}`),
    t(`enums.timeBand.${scenario.timeBand}`),
    t(`enums.hvacState.${scenario.hvacState}`),
    tail,
    trafficSummary(scenario.trafficLevel, t),
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
};

/** `1h 17m` — durations are entered in seconds but nobody reads them that way. */
export const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) {
    return '—';
  }

  const wholeMinutes = Math.round(seconds / 60);
  const hours = Math.floor(wholeMinutes / 60);
  const minutes = wholeMinutes % 60;

  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

/** Postgres hands back `HH:MM:SS`; a test log only cares about `HH:MM`. */
export const formatClockTime = (value: string | null): string => (value === null ? '—' : value.slice(0, 5));
